import { SessionProvider } from 'next-auth/react';
import Head from 'next/head';
import { DFPSlotsProvider } from 'react-dfp';
import { I18nextProvider } from 'react-i18next';
import { BreakpointProvider } from '../bookings/util/useBreakpoints';
import { AnalyticsDispatchers } from '../common/AnalyticsContext/AnalyticsDispatchers/AnalyticsDispatchers';
import { AnalyticsProvider } from '../common/AnalyticsContext/AnalyticsProvider';
import { InitialPageLoadEvent } from '../common/AnalyticsContext/InitialPageLoadEvent';
import { CTAProvider } from '../common/CTAProvider/CTAProvider';
import { CorporateGroupIdentityConfigProvider } from '../common/CorporateGroupIdentityConfigProvider/CorporateGroupIdentityConfigProvider';
import { CorporateUserProvider } from '../common/CorporateUserProvider/CorporateUserProvider';
import { ExperimentProvider } from '../common/ExperimentContext/ExperimentProvider';
import { TargetCookieFixer } from '../common/ExperimentContext/TargetCookieFixer/TargetCookieFixer';
import { FavouritesProvider } from '../common/FavouritesProvider/FavouritesProvider';
import { SentryErrorBoundary } from '../common/Sentry';
import { UserProvider } from '../common/UserProvider/UserProvider';
import { SearchProvider } from '../common/hooks/useSearch';
import { ScrollLockProvider } from '../common/hooks/useStatefulScrollLock';
import { PerformanceReporter } from '../common/performance/PerformanceReporter';
import { HannibalProvider } from '../common/schema/hannibal/HannibalProvider';
import { MegatronProvider } from '../common/schema/megatron/MegatronContext';
import { useServices } from '../common/services/Services';
import { HeThemeProvider } from '../common/services/theme/HeThemeProvider';
import { GlobalStyles } from '../common/styles/GlobalStyles';
import { ChildrenOnly } from '../common/types/ChildrenOnly';
import { env } from '../common/util/env';
import { AdobeLaunchInstaller } from './AdobeLaunchInstaller';
import { DarkModeDetector } from './DarkModeDetector';
import { DisplayRoot } from './DisplayRoot';
import { RootContextProvider } from './RootContext';

export const AppWrappers = ({ children }: ChildrenOnly) => (
  /** Outer error boundary to catch errors in wrappers */
  <SentryErrorBoundary>
    <Head>
      <meta name="commit-sha" content={env.COMMIT_SHA} />
    </Head>

    <ExperimentProvider>
      <RootContextProvider>
        <HeThemeProvider>
          <AdobeLaunchInstaller />
          <GlobalStyles />

          <AnalyticsProvider>
            <PerformanceReporter />
            <AnalyticsDispatchers />
            <InitialPageLoadEvent />
            <DarkModeDetector />
            <MegatronProvider>
              <HannibalProvider>
                <ScrollLockProvider>
                  <BreakpointProvider>
                    <SessionProvider>
                      <CorporateGroupIdentityConfigProvider>
                        <CorporateUserProvider>
                          <UserProvider>
                            <FavouritesProvider>
                              <CTAProvider>
                                <DFPSlotsProvider
                                  dfpNetworkId={env.DFP_NETWORK_ID}
                                  singleRequest={false}
                                  collapseEmptyDivs
                                  lazyLoad={{
                                    fetchMarginPercent: 100,
                                    renderMarginPercent: 25,
                                    mobileScaling: 2.0,
                                  }}
                                >
                                  <SearchProvider>
                                    <DisplayRoot>
                                      <I18nextProvider i18n={useServices().i18n.i18nextInstance}>
                                        <TargetCookieFixer />

                                        {/** Inner error boundary to catch errors in the page */}
                                        <SentryErrorBoundary>{children}</SentryErrorBoundary>
                                      </I18nextProvider>
                                    </DisplayRoot>
                                  </SearchProvider>
                                </DFPSlotsProvider>
                              </CTAProvider>
                            </FavouritesProvider>
                          </UserProvider>
                        </CorporateUserProvider>
                      </CorporateGroupIdentityConfigProvider>
                    </SessionProvider>
                  </BreakpointProvider>
                </ScrollLockProvider>
              </HannibalProvider>
            </MegatronProvider>
          </AnalyticsProvider>
        </HeThemeProvider>
      </RootContextProvider>
    </ExperimentProvider>
  </SentryErrorBoundary>
);
